import {
  Button,
  Field,
  Input,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../../api/HttpClientContext.js';
import { useEvaluationRunHistory } from '../../../hooks/useEvaluationRunHistory/useEvaluationRunHistory.js';
import type { Grader } from '../../../hooks/useGraders/useGraders.interface.js';
import { useGraderVersions } from '../../../hooks/useGraderVersions/useGraderVersions.js';
import { EvaluationRunsService } from '../../../services/evaluationRunsService.js';
import { EvaluationTemplateNotFoundError } from '../../../services/evaluationTemplatesService.interface.js';
import { EvaluationTemplatesService } from '../../../services/evaluationTemplatesService.js';
import { isValidAgentName } from '../../../types/agent.js';
import { EvaluateJobsRail } from '../../AgentsPage/AgentDetailView/AgentEvaluateTab/EvaluateJobsRail.js';
import { JobResultDialog } from '../../EvaluationTemplatesPage/EvaluationTemplateDetailView/JobResultDialog.js';
import type { EvaluationTemplateWithConfig } from '../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.types.js';
import { useEvaluationTemplateCatalogs } from '../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useEvaluationTemplateCatalogs.js';

import { useStyles as useFormStyles } from './GraderEvaluateTab/GraderEvaluateFormColumn.style.js';
import { useStyles } from './GraderEvaluateTab/GraderEvaluateTab.style.js';
import { backingResourceName } from './GraderEvaluateTab/GraderEvaluateTab.utils.js';
import {
  buildTypeScriptEvaluation,
  evaluationInputs,
  validateTrialInputs,
} from './TypeScriptEvaluateTab.utils.js';

interface Props {
  grader: Grader;
  workspaceName: string;
}

/** Run the TypeScript grader as the task's sole grader, not as the agent under test. */
export function TypeScriptEvaluateTab({ grader, workspaceName }: Props) {
  const styles = useStyles();
  const formStyles = useFormStyles();
  const client = useHttpClient();
  const templates = useMemo(
    () => new EvaluationTemplatesService(client),
    [client],
  );
  const runsService = useMemo(
    () => new EvaluationRunsService(client),
    [client],
  );
  const resourceName = useMemo(
    () => backingResourceName(grader.name),
    [grader.name],
  );
  const [agentName, setAgentName] = useState('');
  const [queryText, setQueryText] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  const [running, setRunning] = useState(false);
  const [resultRunId, setResultRunId] = useState<string | null>(null);
  const selectedAgents = useMemo(
    () => (agentName ? [agentName] : []),
    [agentName],
  );
  const { agents, agentsLoading, agentsError } = useEvaluationTemplateCatalogs({
    viewOnly: false,
    activeWorkspaceName: workspaceName,
    selectedAgentNames: selectedAgents,
  });
  const {
    versions,
    isLoading: versionsLoading,
    error: versionsError,
  } = useGraderVersions({ workspaceName, graderName: grader.name });
  const agent = agents.find((item) => item.name === agentName);
  const graderVersion = versions.reduce(
    (latest, version) => Math.max(latest, version.version),
    0,
  );
  const defaults = useMemo(
    () => evaluationInputs(grader, agent),
    [grader, agent],
  );
  const inputs = useMemo(
    () => ({ ...defaults, ...overrides }),
    [defaults, overrides],
  );
  const history = useEvaluationRunHistory({
    workspaceName,
    resourceName,
    runsService,
  });
  const { prependRun, refresh } = history;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    templates
      .get(workspaceName, resourceName)
      .then((template) => {
        if (cancelled) return;
        const saved = template as EvaluationTemplateWithConfig;
        // Only restore templates from this native-grader flow. An older
        // grader-as-agent comparison template must never be silently rewritten.
        if (
          saved.tasks?.[0]?.graders?.length !== 1 ||
          saved.tasks[0].graders[0]?.name !== grader.name
        ) {
          setLoadError(
            'An incompatible evaluation template already uses this name.',
          );
          return;
        }
        setAgentName(saved.tasks[0].agent?.name ?? '');
        setQueryText(
          JSON.stringify(
            saved.querySets?.[0]?.items?.map((row) => {
              try {
                return JSON.parse(row) as unknown;
              } catch {
                return row;
              }
            }) ?? [],
            null,
            2,
          ),
        );
        const bindings =
          (
            saved.tasks[0].graders[0] as {
              with?: Record<string, string>;
            }
          ).with ?? {};
        setOverrides({
          ...Object.fromEntries(
            Object.entries(saved.variables ?? {}).map(([name, value]) => [
              name,
              value.default === undefined
                ? ''
                : typeof value.default === 'string'
                  ? value.default
                  : (JSON.stringify(value.default) ?? ''),
            ]),
          ),
          ...bindings,
        });
      })
      .catch((error: unknown) => {
        if (!cancelled && !(error instanceof EvaluationTemplateNotFoundError)) {
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Could not load evaluation settings.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [templates, workspaceName, resourceName, grader.name]);

  const validation = validateTrialInputs(queryText, inputs, {
    ...agent?.variables,
    ...grader.variables,
  });
  const canRun =
    !loading &&
    !loadError &&
    !running &&
    !agentsLoading &&
    !versionsLoading &&
    !agentsError &&
    !versionsError &&
    isValidAgentName(agentName) &&
    agent?.version !== undefined &&
    graderVersion > 0 &&
    validation === null;

  const handleRun = useCallback(async () => {
    if (!canRun || !agent?.version || graderVersion === 0) return;
    setRunning(true);
    setRunError(null);
    setQueued(false);
    try {
      const body = buildTypeScriptEvaluation(
        grader,
        { ...agent, version: agent.version },
        queryText,
        inputs,
      );
      // Pin the exact grader snapshot resolved by the catalog, rather than a
      // possibly different version made current during template submission.
      const task = (body as { tasks: { graders: { version?: number }[] }[] })
        .tasks[0]!;
      task.graders[0]!.version = graderVersion;
      const template = await templates.replace(
        workspaceName,
        resourceName,
        body,
      );
      if (template.isDraft)
        await templates.snapshot(workspaceName, resourceName);
      const run = await runsService.queue(workspaceName, {
        runOrigin: 'hosted',
        templateName: resourceName,
        displayName: resourceName,
      });
      prependRun(run);
      refresh();
      setQueued(true);
    } catch (error) {
      setRunError(
        error instanceof Error ? error.message : 'Failed to queue evaluation.',
      );
    } finally {
      setRunning(false);
    }
  }, [
    canRun,
    agent,
    graderVersion,
    grader,
    queryText,
    inputs,
    templates,
    workspaceName,
    resourceName,
    runsService,
    prependRun,
    refresh,
  ]);

  if (loading) return <Spinner label="Loading evaluation…" />;

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={formStyles.formColumn}>
          <section className={formStyles.card}>
            <div className={formStyles.cardLabel}>Grader under test</div>
            <strong>{grader.name}</strong>
            <span className={formStyles.graderHint}>
              This TypeScript grader scores the selected agent&apos;s output
              directly. Its own metrics appear in the run results; no comparison
              grader is needed.
            </span>
          </section>
          <section className={formStyles.card}>
            <label htmlFor="ts-trial-agent" className={formStyles.cardLabel}>
              Trial agent
            </label>
            <select
              id="ts-trial-agent"
              value={agentName}
              disabled={running}
              onChange={(event) => {
                setAgentName(event.target.value);
                setOverrides({});
              }}
            >
              <option value="">Select a snapshotted agent</option>
              {agents.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.displayName ?? item.name}
                </option>
              ))}
            </select>
            <span className={formStyles.graderHint}>
              Choose an existing agent (including a synthetic test agent) to
              produce each trial&apos;s output. The grader may read that output
              through its own invocation expressions.
            </span>
          </section>
          <section className={formStyles.card}>
            <label htmlFor="ts-trial-rows" className={formStyles.cardLabel}>
              Query rows
            </label>
            <Textarea
              id="ts-trial-rows"
              value={queryText}
              disabled={running}
              placeholder={
                '[{"userQuery":"Example question","response":"Example answer"}]'
              }
              onChange={(_, data) => setQueryText(data.value)}
            />
            <span className={formStyles.graderHint}>
              JSON array or JSONL of objects; one trial per row.
            </span>
          </section>
          {Object.keys(inputs).length > 0 && (
            <section className={formStyles.card}>
              <div className={formStyles.cardLabel}>Runtime inputs</div>
              <span className={formStyles.graderHint}>
                Bind a variable to a query-row field or enter a literal. For
                LLM-backed graders, provide your tenant&apos;s actual endpoint
                and model if referenced. Code-only graders do not need a model.
              </span>
              <div className={formStyles.inputStack}>
                {Object.entries(inputs).map(([name, value]) => (
                  <Field
                    key={name}
                    className={formStyles.inputRow}
                    label={name}
                  >
                    <Input
                      className={formStyles.fullWidthInput}
                      value={value}
                      disabled={running}
                      onChange={(_, data) =>
                        setOverrides((previous) => ({
                          ...previous,
                          [name]: data.value,
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </section>
          )}
          {loadError && <p role="alert">{loadError}</p>}
          {agentsError && <p role="alert">{agentsError}</p>}
          {versionsError && (
            <p role="alert">Could not load grader snapshots: {versionsError}</p>
          )}
          {!agentsLoading && agentName && !agent && (
            <p role="alert">Trial agent not found.</p>
          )}
          {!versionsLoading && !versionsError && graderVersion === 0 && (
            <p role="alert">
              This grader has no immutable snapshot. Create a snapshot before
              evaluating; the hosted runner cannot use an unsnapshotted draft.
            </p>
          )}
          {!agentsLoading && agent && agent.version === undefined && (
            <p role="alert">Snapshot the trial agent before evaluating.</p>
          )}
          {validation && <p role="status">{validation}</p>}
          {runError && <p role="alert">{runError}</p>}
          {queued && (
            <p role="status">
              Evaluation queued. Check its job status and results below.
            </p>
          )}
          <Button
            appearance="primary"
            disabled={!canRun}
            onClick={() => void handleRun()}
          >
            {running ? 'Starting…' : 'Run evaluation'}
          </Button>
        </div>
        <EvaluateJobsRail
          emptyMessage="No runs yet. Choose a trial agent, supply query rows and required inputs, then run this grader."
          runHistory={history.runHistory}
          loading={history.loading}
          error={history.error}
          cancellingId={history.cancellingId}
          onCancelRun={(id) => history.cancelRun(id)}
          onOpenResult={setResultRunId}
        />
      </div>
      <JobResultDialog
        workspaceName={workspaceName}
        runId={resultRunId}
        runsService={runsService}
        onClose={() => setResultRunId(null)}
      />
    </div>
  );
}
